import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  Agreement,
  ApplicationConfig,
  ApplicationQuestion,
  ApplicationSection,
  ChildcareSituation,
  CommonQuestionKeys,
  Confirmation,
  EmergencyCategories,
  FieldType,
  FoodChalleges,
  Frequency,
  HousingSituation,
  LearningSituation,
  Likelihood,
  PhysiologicChallengeFrequency,
  PhysiologicSituationPastMonth,
  SleepingSituation,
  SpecialFieldKeys,
  TextInputType,
  TransportationSituation,
} from '@bybeam/platform-types';
import { and, eq, gte, inList, lte, or } from '../../ast/utils.js';
import STANDARD_COMPONENTS from '../standards/index.js';

// This isn't imported anywhere but needs to be exported or typescript complains:
// https://github.com/microsoft/TypeScript/issues/5711
export interface EduConfig extends ApplicationConfig {
  definitions: {
    sections: { optionalDocumentation: ApplicationSection };
    questions: {
      believesFundsWouldHelp: ApplicationQuestion;
    };
  };
}

export function makeEduConfig(): EduConfig {
  const DOCUMENT_QUESTION: ApplicationQuestion = {
    key: 'documents',
    copy: {
      title: 'Supporting Documents',
      intro:
        'Please upload supporting documents related to the challenge area(s) indicated in your application. This is a required step.',
    },
    layout: 'panel',
    fields: [
      {
        type: FieldType.Document,
        key: 'documents.all',
        copy: {
          title: 'Supporting Documents',
          description: `
Documents should clearly indicate the name and contact information of the company or payee, 
along with your name and account information. After you scan your document(s), please preview 
it to make sure that all key information is visible before uploading it. Ideally, the documents 
uploaded were received in the last 30 days.

The following types of documentation can be uploaded for our review process (but are not limited to):

- Current signed lease in your name (or your name on the lease) or a notarized statement from your landlord/rental company
- Current mortgage statement
- Itemized car repair estimate
- Car loan payment statement
- Utility bill
- Childcare`,
        },
      },
    ],
  };

  const QUESTIONS: { [key: string]: ApplicationQuestion } = {
    [CommonQuestionKeys.EMAIL]: {
      key: CommonQuestionKeys.EMAIL,
      copy: { title: 'What is your student email address?' },
      fields: [
        {
          type: FieldType.Text,
          inputType: TextInputType.Email,
          copy: 'Email Address',
          key: CommonQuestionKeys.EMAIL,
        },
      ],
    },
    [CommonQuestionKeys.DATE_OF_BIRTH]:
      STANDARD_COMPONENTS.definitions.questions[CommonQuestionKeys.DATE_OF_BIRTH],
    studentId: {
      key: 'studentId',
      copy: { title: 'What is your student ID?' },
      fields: [
        {
          type: FieldType.Text,
          inputType: TextInputType.Text,
          copy: 'Student ID',
          key: 'studentId',
        },
      ],
    },
    graduationDate: {
      key: 'graduationDate',
      copy: {
        title: 'What month and year do you expect to finish college?',
        intro: 'Please provide an estimate. No need to look up dates.',
      },
      fields: [
        {
          type: FieldType.Date,
          copy: 'Expected Graduation Date',
          key: 'graduationDate',
          validation: { required: true, allowedRange: 'future' },
          props: { helperText: 'Example: 06/01/2024' },
        },
      ],
    },
    filedFafsa: {
      key: 'filedFafsa',
      copy: { title: 'Have you filed a FAFSA for the current academic year?' },
      fields: [
        {
          type: FieldType.RadioList,
          key: 'filedFafsa',
          options: [
            { label: 'Yes', value: Confirmation.yes },
            { label: 'No', value: Confirmation.no },
          ],
        },
      ],
    },
    hasDependants: {
      key: 'hasDependants',
      copy: {
        title: 'Are you currently responsible for the care of any minors (aged 17 or under)?',
      },
      fields: [
        {
          type: FieldType.RadioList,
          key: 'hasDependants',
          options: [
            { label: 'Yes', value: Confirmation.yes },
            { label: 'No', value: Confirmation.no },
          ],
        },
        {
          type: FieldType.Typography,
          key: 'youngestDependantAge.question',
          dynamicLogic: eq('hasDependants', 'yes'),
          copy: 'What is the age of the youngest child?',
          props: { variant: 'largeBody', alignment: 'center' },
        },
        {
          type: FieldType.Dropdown,
          copy: 'Youngest Child’s Age',
          key: 'youngestDependantAge',
          dynamicLogic: eq('hasDependants', 'yes'),
          options: [
            { label: '0', value: '0' },
            { label: '1', value: '1' },
            { label: '2', value: '2' },
            { label: '3', value: '3' },
            { label: '4', value: '4' },
            { label: '5', value: '5' },
            { label: '6', value: '6' },
            { label: '7', value: '7' },
            { label: '8', value: '8' },
            { label: '9', value: '9' },
            { label: '10+', value: '10+' },
          ],
        },
      ],
    },
    dropoutLikelihood: {
      key: 'dropOutLikelihood',
      copy: {
        title: 'How likely are you to drop out this semester due to your financial emergency?',
      },
      fields: [
        {
          type: FieldType.RadioList,
          key: 'dropOutLikelihood',
          options: [
            {
              value: Likelihood.veryLikely,
              label: 'Very likely to drop out',
            },
            {
              value: Likelihood.likely,
              label: 'Likely to drop out',
            },
            {
              value: Likelihood.neutral,
              label: 'I don’t know',
            },
            {
              value: Likelihood.unlikely,
              label: 'Unlikely to drop out',
            },
            {
              value: Likelihood.veryUnlikely,
              label: 'Very unlikely',
            },
          ],
        },
      ],
    },
    emergencyCategories: {
      key: 'applicableEmergencyCategories',
      copy: { title: 'In what areas are you struggling?' },
      fields: [
        {
          type: FieldType.CheckboxGroup,
          key: 'applicableEmergencyCategories',
          options: [
            {
              value: EmergencyCategories.food,
              // TODO: Should these be label/description?
              label: 'Food - I.e. unable to afford or access healthy meals for yourself or others',
            },
            {
              value: EmergencyCategories.housing,
              label: 'Housing - I.e. unaffordable housing costs, homelessness, etc.',
            },
            {
              value: EmergencyCategories.childcare,
              label: 'Childcare - I.e. unable to afford or access necessary childcare',
            },
            {
              value: EmergencyCategories.transportation,
              label:
                'Transportation - I.e. car trouble, lack of transportation, unaffordable costs, etc.',
            },
            {
              value: EmergencyCategories.health,
              label: 'Health - I.e. unaffordable healthcare costs, health crises, etc.',
            },
            {
              value: EmergencyCategories.safety,
              label:
                'Safety and Security - I.e. worried about violence against you or a loved one, immigration concerns, etc.',
            },
            {
              value: EmergencyCategories.learning,
              label: 'Learning Resources - I.e. unable to afford books, lab fees, printing, etc.',
            },
            { value: EmergencyCategories.none, label: 'None of the above' },
          ],
        },
      ],
    },
    foodPersistence: {
      key: 'foodPersistence',
      copy: {
        title: 'In the last 30 days, how often was the following statement true?',
        intro: '“The food that I bought just didn’t last, and I didn’t have money to get more.”',
      },
      fields: [
        {
          type: FieldType.RadioList,
          key: 'foodPersistence',
          options: [
            {
              value: Frequency.often,
              label: 'Often true',
            },
            {
              value: Frequency.sometimes,
              label: 'Sometimes true',
            },
            {
              value: Frequency.never,
              label: 'Never true',
            },
          ],
        },
      ],
    },
    unbalancedMeals: {
      key: 'unbalancedMeals',
      copy: {
        title: 'In the last 30 days, how often was the following statement true?',
        intro: '“I couldn’t afford to eat balanced meals.”',
      },
      fields: [
        {
          type: FieldType.RadioList,
          key: 'unbalancedMeals',
          options: [
            {
              value: Frequency.often,
              label: 'Often true',
            },
            {
              value: Frequency.sometimes,
              label: 'Sometimes true',
            },
            {
              value: Frequency.never,
              label: 'Never true',
            },
          ],
        },
      ],
    },
    skippedMeals: {
      key: 'skippedMealsInPastMonth',
      copy: {
        title:
          'In the last 30 days, did you ever cut the size of your meals or skip meals because there wasn’t enough money for food?',
      },
      fields: [
        {
          type: FieldType.RadioList,
          key: 'hasSkippedMealsInPastMonth',
          options: [
            { label: 'Yes', value: Confirmation.yes },
            { label: 'No', value: Confirmation.no },
          ],
        },
        {
          type: FieldType.Typography,
          key: 'skippedMealsInPastMonth.question',
          dynamicLogic: eq('hasSkippedMealsInPastMonth', 'yes'),
          copy: 'How many days did this happen in the past 30 days?',
          props: { variant: 'largeBody', alignment: 'center' },
        },
        {
          type: FieldType.Text,
          key: 'skippedMealsInPastMonth',
          dynamicLogic: eq('hasSkippedMealsInPastMonth', 'yes'),
          copy: 'Number of days in the past 30 days',
          inputType: TextInputType.Number,
          validation: {
            required: true,
            rules: [
              {
                rule: and([gte('skippedMealsInPastMonth', 1), lte('skippedMealsInPastMonth', 30)]),
                message: 'Enter a valid amount of days',
              },
            ],
          },
        },
      ],
    },
    foodChallenges: {
      key: 'foodChallenges',
      copy: { title: 'Are any of the following true about the past 30 days?' },
      fields: [
        {
          type: FieldType.CheckboxGroup,
          key: 'foodChallenges',
          options: [
            {
              value: FoodChalleges.insufficientFunds,
              label:
                "You ate less than you felt you should because there wasn't enough money for food",
            },
            { value: FoodChalleges.borrowedFunds, label: 'You borrowed money for food' },
            {
              value: FoodChalleges.wentHungry,
              label: "You were hungry but didn't eat because there wasn't enough money for food",
            },
            {
              value: FoodChalleges.attendedEventForFood,
              label: 'You attended an event simply for the free food',
            },
            {
              value: FoodChalleges.unhealthyFood,
              label: 'You purchased most of your meals from a gas station or convenience store',
            },
            { value: FoodChalleges.none, label: 'None of the above' },
          ],
        },
      ],
    },
    hasBeenHomeless: {
      key: 'hasBeenHomeless',
      copy: { title: 'Since starting college, have you ever been homeless?' },
      fields: [
        {
          type: FieldType.RadioList,
          key: 'hasBeenHomeless',
          options: [
            { label: 'Yes', value: Confirmation.yes },
            { label: 'No', value: Confirmation.no },
          ],
        },
      ],
    },
    sleepingSituation: {
      key: 'sleepingSituation',
      copy: {
        title:
          'In the past 30 days, have you slept in any of the following places because you lacked other options?',
      },
      fields: [
        {
          type: FieldType.CheckboxGroup,
          key: 'sleepingSituation',
          options: [
            { value: SleepingSituation.shelter, label: 'A shelter' },
            {
              value: SleepingSituation.camper,
              label: 'A vehicle (car, truck, van, etc.)',
            },
            {
              value: SleepingSituation.couchSurfing,
              label: 'At a relative’s, friend’s, or stranger’s temporarily (e.g. couch surfing)',
            },
            {
              value: SleepingSituation.hotel,
              label:
                'A hotel or motel without a permanent home to return to (not on vacation or business travel)',
            },
            {
              value: SleepingSituation.groupHome,
              label: 'A group home or transitional housing (e.g. halfway house)',
            },
            {
              value: SleepingSituation.treatmentCenter,
              label: 'A treatment center (rehab center, hospital, etc.)',
            },
            {
              value: SleepingSituation.outdoorsCovered,
              label:
                'An outdoor location (sidewalk, alley, bus or train stop, park, under highway, etc.)',
            },
            {
              value: SleepingSituation.outdoors,
              label:
                'A space not meant for human habitation (abandoned building, encampment, tent, etc.)',
            },
            { value: SleepingSituation.none, label: 'None of the above' },
          ],
        },
      ],
    },
    housingSituation: {
      key: 'housingSituation',
      copy: { title: 'Are any of the following true about the past 30 days?' },
      fields: [
        {
          type: FieldType.CheckboxGroup,
          key: 'housingSituation',
          options: [
            {
              value: HousingSituation.rentRaised,
              label: 'There was a rent or mortgage increase that made it difficult to pay',
            },
            {
              value: HousingSituation.behindOnRent,
              label: 'You did not pay or underpaid your rent or mortgage',
            },
            {
              value: HousingSituation.struggledToPay,
              label: 'You struggled to pay your rent or mortgage',
            },
            { value: 'moved-often', label: 'You moved two or more times' },
            {
              value: HousingSituation.shortedBills,
              label:
                'You did not pay the full amount of a utility (gas, oil, water, or electricity) bill',
            },
            {
              value: HousingSituation.temporaryHousing,
              label:
                'You moved in with other people, even for a little while, because of financial problems',
            },
            {
              value: HousingSituation.compactLiving,
              label:
                'You lived with more people than the expected capacity of the house or apartment (i.e. a room intended for 1, with more than 1 person living in it)',
            },
            { value: HousingSituation.none, label: 'None of the above' },
          ],
        },
      ],
    },
    childcareSituation: {
      key: 'childcareSituation',
      copy: { title: 'Are any of the following true about the past 30 days?' },
      fields: [
        {
          type: FieldType.CheckboxGroup,
          key: 'childcareSituation',
          options: [
            {
              value: ChildcareSituation.affordNeeds,
              label: 'You were unable or struggled to pay for diapers',
            },
            {
              value: ChildcareSituation.missedWork,
              label: 'You had to miss work or classes for a sick or suspended child',
            },
            {
              value: ChildcareSituation.supbarChildcare,
              label:
                'You were unable to pay for quality childcare so that you could attend college',
            },
            { value: ChildcareSituation.none, label: 'None of the above' },
          ],
        },
      ],
    },
    transportationSituation: {
      key: 'transportationSituation',
      copy: { title: 'Are any of the following true about the past 30 days?' },
      fields: [
        {
          type: FieldType.CheckboxGroup,
          key: 'transportationSituation',
          options: [
            {
              value: TransportationSituation.bikeStolen,
              label: 'Your bike was stolen or needs repair',
            },
            { value: TransportationSituation.licenseRevoked, label: 'Your license was revoked' },
            { value: TransportationSituation.carStolen, label: 'Your car was stolen' },
            {
              value: TransportationSituation.carRepair,
              label: 'Your car needs a big repair (engine problem, crash result, etc.)',
            },
            {
              value: TransportationSituation.insufficientFunds,
              label: 'You are unable to afford gas or public transportation',
            },
            {
              value: TransportationSituation.behindOnPayments,
              label: 'You fell behind on car or insurance payments',
            },
            { value: TransportationSituation.none, label: 'None of the above' },
          ],
        },
      ],
    },
    physiologicSituationPastMonth: {
      key: 'physiologicSituationPastMonth',
      copy: {
        title:
          'In the past 30 days, how many days have you felt that emotional, mental, or physical difficulties have hurt your academic performance?',
      },
      fields: [
        {
          type: FieldType.RadioList,
          key: 'physiologicSituationPastMonth',
          options: [
            { label: 'None', value: PhysiologicChallengeFrequency.none },
            { label: '1-2 days', value: PhysiologicChallengeFrequency.oneOrTwoDays },
            { label: '3-5 days', value: PhysiologicChallengeFrequency.threeToFiveDays },
            { label: '6 or more days', value: PhysiologicChallengeFrequency.sixOrMoreDays },
          ],
        },
      ],
    },
    physiologicSituation: {
      key: 'physiologicSituation',
      copy: { title: 'Are any of the following true about the past 30 days?' },
      fields: [
        {
          type: FieldType.CheckboxGroup,
          key: 'physiologicSituation',
          options: [
            {
              value: PhysiologicSituationPastMonth.avoidedDoctor,
              label: 'You avoided the doctor for an issue due to financial costs',
            },
            {
              value: PhysiologicSituationPastMonth.unexpectedMedicalExpense,
              label: 'You received an unexpected medical bill',
            },
            {
              value: PhysiologicSituationPastMonth.leftUnsafeHousing,
              label: 'You have been in or left an unsafe housing situation',
            },
            {
              value: PhysiologicSituationPastMonth.skippedMedication,
              label: 'You skipped or didn’t pick up medication due to cost',
            },
            {
              value: PhysiologicSituationPastMonth.none,
              label: 'None of the above',
            },
          ],
        },
      ],
    },
    dacaEligible: {
      key: 'dacaEligible',
      copy: {
        title:
          'Do you believe that you are eligible for DACA (Deferred Action for Childhood Arrivals) or the DREAM Act?',
      },
      fields: [
        {
          type: FieldType.RadioList,
          key: 'dacaEligible',
          options: [
            { label: 'Yes', value: Confirmation.yes },
            { label: 'No', value: Confirmation.no },
          ],
        },
      ],
    },
    learningSituation: {
      key: 'learningSituation',
      copy: { title: 'Are any of the following true about the past 30 days?' },
      fields: [
        {
          type: FieldType.CheckboxGroup,
          key: 'learningSituation',
          options: [
            {
              label: 'You do not have enough money for required books and supplies',
              value: LearningSituation.cannotAffordBooks,
            },
            {
              label: 'You do not have enough money for helpful supplies',
              value: LearningSituation.cannotAffordSupplies,
            },
            {
              label: 'Your computer broke and you cannot replace it',
              value: LearningSituation.computerBroken,
            },
            { label: 'None of the above', value: LearningSituation.none },
          ],
        },
      ],
    },
    hasOutsideSupport: {
      key: 'hasOutsideSupport',
      copy: {
        title: 'To what extent do you agree with this statement?',
        intro: '“I have support or access to resources that can help me in an emergency.”',
      },
      fields: [
        {
          type: FieldType.RadioList,
          key: 'hasOutsideSupport',
          options: [
            {
              value: Agreement.stronglyAgree,
              label: 'Strongly agree',
            },
            {
              value: Agreement.agree,
              label: 'Agree',
            },
            {
              value: Agreement.neutral,
              label: 'Neutral',
            },
            {
              value: Agreement.disagree,
              label: 'Disagree',
            },
            {
              value: Agreement.stronglyDisagree,
              label: 'Strongly disagree',
            },
          ],
        },
      ],
    },
    requestedAmount: {
      key: 'requestedAmount',
      copy: {
        title: 'Financial Assistance Request',
        intro:
          'Please enter the total amount (up to {MAX_AMOUNT}) of emergency cash assistance you believe will cover the total amount of expenses you have.',
      },
      fields: [
        {
          type: FieldType.Text,
          inputType: TextInputType.Currency,
          copy: 'Amount Requested',
          key: SpecialFieldKeys.RequestedAmount,
          props: { helperText: 'Max limit = {MAX_AMOUNT}' },
        },
      ],
    },
    hasBankAccount: {
      key: 'hasBankAccount',
      copy: {
        title: 'Do you have an active bank account in the U.S.?',
        intro:
          'Your banking status does not impact our decision making. If you do not have a bank account, you can receive funds via a prepaid card.',
      },
      fields: [
        {
          type: FieldType.RadioList,
          key: 'hasBankAccount',
          options: [
            {
              value: Confirmation.yes,
              label: 'I have a U.S. bank account',
            },
            {
              value: Confirmation.no,
              label: 'I do not have a U.S. bank account',
            },
          ],
        },
        {
          type: FieldType.Typography,
          key: 'hasBankAccount.confirmation',
          dynamicLogic: eq('hasBankAccount', 'yes'),
          copy: 'Please confirm your banking status in the U.S. by selecting the checkbox below.',
          props: { variant: 'largeBody', alignment: 'center' },
        },
        {
          type: FieldType.Checkbox,
          key: 'confirmBankingStatus',
          dynamicLogic: eq('hasBankAccount', 'yes'),
          copy: 'I, {APPLICANT_NAME}, understand and confirm my banking status response to be correct.',
        },
      ],
    },
    otherInformation: {
      key: 'otherInformation',
      copy: {
        title: 'Is there anything the we forgot to ask about?',
        intro:
          "This space is available to you if there is any other information you'd like to share with us that we may have missed.",
      },
      fields: [
        {
          type: FieldType.Text,
          inputType: TextInputType.Text,
          key: 'otherInformation',
          copy: 'Comments (optional)',
          props: { multiline: true, rows: 5 },
          validation: { required: false },
        },
      ],
    },
    applicationDocumentation: DOCUMENT_QUESTION,
    [CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER]: {
      key: CommonQuestionKeys.APPLICATION_ASSISTANCE_HELPER,
      copy: {
        title: 'Did someone help you fill out this application?',
        intro:
          'Please provide the following information about the person that helped you complete this application.',
      },
      skippable: true,
      fields: [
        {
          type: FieldType.Text,
          inputType: TextInputType.Text,
          copy: 'Helper Name',
          key: 'submission.assistance.helper.name',
          validation: { required: false },
        },
        {
          type: FieldType.Text,
          inputType: TextInputType.Text,
          copy: 'Organization (if applicable)',
          key: 'submission.assistance.helper.organization',
          validation: { required: false },
        },
        {
          type: FieldType.Text,
          inputType: TextInputType.Email,
          copy: 'Helper Email',
          key: 'submission.assistance.helper.email',
          validation: { required: false },
        },
        {
          type: FieldType.Text,
          inputType: TextInputType.Phone,
          copy: 'Helper Phone Number',
          key: 'submission.assistance.helper.phone',
          validation: { required: false },
        },
      ],
    },
    affirmation: {
      key: CommonQuestionKeys.AFFIRMATION,
      copy: {
        title: 'Before submitting the application, please confirm this statement:',
        intro: 'Please review and confirm the statement by selecting the checkbox below.',
      },
      fields: [
        {
          type: FieldType.Checkbox,
          key: CommonQuestionKeys.AFFIRMATION,
          copy: "I, {APPLICANT_NAME}, certify under penalty of law, based on information and belief formed after reasonable inquiry, the statements and information contained in these documents are true, accurate and complete. I acknowledge that my information can be used by {PARTNER_NAME}'s staff to refer me to other programs.",
        },
      ],
    },
  };

  const COMMON_SECTIONS: ApplicationSection[] = [
    {
      key: 'personalInfo',
      name: 'Personal Info',
      overview: {
        description: `
In this section, you’ll let us know what month and year you expect to graduate, if
you’ve filed a FAFSA for the current academic year, if you receive financial aid and if
you’re responsible for taking care of any minors.`,
        title: 'Let’s start with some personal information about you',
      },
      questionGroups: [
        {
          key: 'studentInfo',
          name: 'Student Info',
          questions: [
            QUESTIONS[CommonQuestionKeys.EMAIL],
            QUESTIONS[CommonQuestionKeys.DATE_OF_BIRTH],
            QUESTIONS.studentId,
          ],
        },
        {
          key: 'personalInfo',
          name: 'Personal Info',
          questions: [QUESTIONS.graduationDate, QUESTIONS.filedFafsa, QUESTIONS.hasDependants],
        },
      ],
    },
    {
      key: 'financialChallenges',
      name: 'Financial Challenges',
      overview: {
        description: `
In this section, you’ll let us know about the likelihood of your dropping out and about
areas in which you currently need help with financial assistance. Each area will have
its own questions.`,
        title: 'Let’s learn more about the financial challenges you are facing in school',
      },
      questionGroups: [
        {
          key: 'financialChallenges',
          name: 'Financial Challenges',
          questions: [QUESTIONS.dropoutLikelihood, QUESTIONS.emergencyCategories],
        },
        {
          key: 'food',
          name: 'Food',
          overview: {
            title: "You selected 'Food' as an area in which you are struggling.",
            description: 'We have some additional questions to help us understand your situation.',
          },
          dynamicLogic: inList('applicableEmergencyCategories', 'food'),
          questions: [
            QUESTIONS.foodPersistence,
            QUESTIONS.unbalancedMeals,
            QUESTIONS.skippedMeals,
            QUESTIONS.foodChallenges,
          ],
        },
        {
          key: 'housing',
          name: 'Housing',
          overview: {
            title: "You selected 'Housing' as an area in which you are struggling.",
            description: 'We have some additional questions to help us understand your situation.',
          },
          dynamicLogic: inList('applicableEmergencyCategories', 'housing'),
          questions: [
            QUESTIONS.hasBeenHomeless,
            QUESTIONS.sleepingSituation,
            QUESTIONS.housingSituation,
          ],
        },
        {
          key: 'childcare',
          name: 'Childcare',
          overview: {
            title: "You selected 'Childcare' as an area in which you are struggling.",
            description: 'We have some additional questions to help us understand your situation.',
          },
          dynamicLogic: inList('applicableEmergencyCategories', 'childcare'),
          questions: [QUESTIONS.childcareSituation],
        },
        {
          key: 'transportation',
          name: 'Transportation',
          overview: {
            title: "You selected 'Transportation' as an area in which you are struggling.",
            description: 'We have some additional questions to help us understand your situation.',
          },
          dynamicLogic: inList('applicableEmergencyCategories', 'transportation'),
          questions: [QUESTIONS.transportationSituation],
        },
        {
          key: 'health',
          name: 'Health and/or Safety and Security',
          overview: {
            title:
              "You selected 'Health' and/or 'Safety and Security' as an area in which you are struggling.",
            description: 'We have some additional questions to help us understand your situation.',
          },
          dynamicLogic: or([
            inList('applicableEmergencyCategories', 'health'),
            inList('applicableEmergencyCategories', 'safety'),
          ]),
          questions: [
            QUESTIONS.physiologicSituationPastMonth,
            QUESTIONS.physiologicSituation,
            QUESTIONS.dacaEligible,
          ],
        },
        {
          key: 'learning',
          name: 'Learning Resources',
          overview: {
            title: "You selected 'Learning Resources' as an area in which you are struggling.",
            description: 'We have some additional questions to help us understand your situation.',
          },
          dynamicLogic: inList('applicableEmergencyCategories', 'learning'),
          questions: [QUESTIONS.learningSituation],
        },
      ],
    },
    {
      key: 'youAndYourSituation',
      name: 'You & Your Situation',
      overview: {
        description: `
In this section you’ll select a response to several statements regarding your financial
situation. You’ll also have the opportunity to upload any documents to support your
responses.`,
        title: 'Now it’s time to find out a little more about you and your situation',
      },
      questionGroups: [
        {
          key: 'youAndYourSituation',
          name: 'You & Your Situation',
          questions: [
            QUESTIONS.hasOutsideSupport,
            QUESTIONS.requestedAmount,
            QUESTIONS.hasBankAccount,
            QUESTIONS.otherInformation,
          ],
        },
      ],
    },
    {
      key: 'documents',
      name: 'Document Upload',
      overview: {
        description: `
Your documents will be carefully reviewed. Upload any documents you have available that
you believe will help your eligibility. Documentation is required.`,
        title: 'Your document upload',
      },
      questionGroups: [
        {
          key: 'documents',
          name: 'Documents Uploaded',
          questions: [QUESTIONS.applicationDocumentation],
        },
      ],
    },
    STANDARD_COMPONENTS.definitions.sections.review,
  ];

  return {
    introPages: [
      {
        key: 'what-is-it',
        layout: 'welcome',
        title: 'What is emergency aid?',
        body: `
Emergency aid supports individuals experiencing a financial emergency. These funds do not 
require repayment.

If financial assistance could help you pay for your needs, you may be eligible to submit an 
application through {PROGRAM_NAME}. To learn more, go to [{FAQ_PAGE}]({FAQ_PAGE}).`,
      },
      STANDARD_COMPONENTS.definitions.introPages.ApplicationProcess,
    ],
    sections: COMMON_SECTIONS,
    definitions: {
      sections: {
        optionalDocumentation: mergeObjects(
          [
            COMMON_SECTIONS.find(({ key }) => key === 'documents'),
            {
              overview: {
                description:
                  'Your documents will be carefully reviewed. Upload any documents you have available that you believe will help your eligibility. Documentation is optional.',
              },
              questionGroups: [
                {
                  questions: [
                    {
                      copy: {
                        intro:
                          'Please upload supporting documents related to the challenge area(s) indicated in your application.',
                      },
                      skippable: true,
                    },
                  ],
                },
              ],
            } as ApplicationSection,
          ],
          'merge',
        ),
      },
      questions: {
        believesFundsWouldHelp: {
          key: 'believesFundsWouldHelp',
          copy: {
            title: 'To what extent do you agree with this statement?',
            intro:
              '“This challenge could be greatly reduced or eliminated with $500 or less in financial support.”',
          },
          fields: [
            {
              type: FieldType.RadioList,
              key: 'believesFundsWouldHelp',
              options: [
                { value: Agreement.stronglyAgree, label: 'Strongly agree' },
                { value: Agreement.agree, label: 'Agree' },
                { value: Agreement.neutral, label: 'Neutral' },
                { value: Agreement.disagree, label: 'Disagree' },
                { value: Agreement.stronglyDisagree, label: 'Strongly disagree' },
              ],
            },
          ],
        },
      },
    },
  };
}
export default makeEduConfig();
